// IND-021: «Revisa tus ficheros» agrupado por tipo de aviso (fichero + código)
// con una tabla compacta por tipo. Solo presentación: no cambia los avisos.
import type {
  MissingDataTable,
  PlantDataNotice,
  PlantDataNoticeAffectedSummary,
  PlantDataNoticeLevel,
  PlantDataNoticeTypeTexts,
} from '../services/industrialCostService';
import { formatEsNumber } from './numberFormat';
import { noticeTypeName } from './plantPanel';

const LEVEL_RANK: Record<PlantDataNoticeLevel, number> = { blocks: 0, incomplete: 1, info: 2 };

// Plural corto de cada tipo de documento afectado («12 OF · 2 partes»).
const DOCUMENT_PLURALS: Record<string, [string, string]> = {
  'OF pendiente': ['OF', 'OF'],
  'parte de producción': ['parte', 'partes'],
  producto: ['producto', 'productos'],
  material: ['material', 'materiales'],
  cliente: ['cliente', 'clientes'],
  'fila del escandallo': ['fila', 'filas'],
  'fila de cajas reales': ['fila', 'filas'],
  fila: ['fila', 'filas'],
  'línea de compra': ['línea de compra', 'líneas de compra'],
  documento: ['documento', 'documentos'],
  albarán: ['albarán', 'albaranes'],
  día: ['día', 'días'],
};

// «Descargar lo que falta» de IND-020 para cada tipo con fila a rellenar.
const MISSING_TABLE_BY_CODE: Record<string, MissingDataTable> = {
  cliente_sin_personalizacion: 'personalizaciones',
  caja_sin_caja_real: 'cajas-reales',
  caja_real_no_existe: 'cajas-reales',
  producto_sin_escandallo: 'escandallo',
  material_sin_recuento: 'recuento',
};

function documentLabel(type: string, count: number): string {
  const [one, many] = DOCUMENT_PLURALS[type] ?? [type, type];
  return `${formatEsNumber(count)} ${count === 1 ? one : many}`;
}

/** Suma los resúmenes de varios avisos (por tipo de documento y por unidad). */
export function mergeSummaries(summaries: PlantDataNoticeAffectedSummary[][]): {
  documents: Array<{ document_type: string; count: number }>;
  quantities: Array<{ unit: string; quantity: number }>;
} {
  const documents = new Map<string, number>();
  const quantities = new Map<string, number>();
  for (const summary of summaries) {
    for (const item of summary) {
      documents.set(item.document_type, (documents.get(item.document_type) ?? 0) + item.count);
      if (item.quantity != null && item.unit) {
        quantities.set(item.unit, (quantities.get(item.unit) ?? 0) + item.quantity);
      }
    }
  }
  return {
    documents: Array.from(documents, ([document_type, count]) => ({ document_type, count })),
    quantities: Array.from(quantities, ([unit, quantity]) => ({ unit, quantity })),
  };
}

/** «12 OF · 2 partes · 3.400 m»; «—» si no afecta a ningún documento. */
export function affectedSummaryText(summary: PlantDataNoticeAffectedSummary[] | undefined): string {
  const merged = mergeSummaries([summary ?? []]);
  const parts = [
    ...merged.documents.map((item) => documentLabel(item.document_type, item.count)),
    ...merged.quantities.map((item) => `${formatEsNumber(item.quantity, { maximumFractionDigits: 0 })} ${item.unit}`),
  ];
  return parts.length > 0 ? parts.join(' · ') : '—';
}

function documentsCount(notice: PlantDataNotice): number {
  return (notice.affected_summary ?? []).reduce((total, item) => total + item.count, 0);
}

function quantityTotal(notice: PlantDataNotice): number {
  return (notice.affected_summary ?? []).reduce((total, item) => total + (item.quantity ?? 0), 0);
}

export type NoticeTypeGroup = {
  key: string;
  code: string;
  title: string;
  level: PlantDataNoticeLevel;
  notices: PlantDataNotice[];
  affectedTotal: number;
  newCount: number;
  what: string;
  todo: string;
  totalText: string;
  missingTable: MissingDataTable | null;
};

function commonText(values: string[]): string | null {
  const unique = Array.from(new Set(values.filter(Boolean)));
  return unique.length === 1 ? unique[0] : null;
}

/** Un grupo por código, en orden nivel → afectados → título. */
export function groupFileNotices(
  notices: PlantDataNotice[],
  types: Record<string, PlantDataNoticeTypeTexts> | undefined,
): NoticeTypeGroup[] {
  const byCode = new Map<string, PlantDataNotice[]>();
  for (const notice of notices) {
    const items = byCode.get(notice.code);
    if (items) items.push(notice);
    else byCode.set(notice.code, [notice]);
  }
  const groups = Array.from(byCode, ([code, items]): NoticeTypeGroup => {
    const level = items.reduce<PlantDataNoticeLevel>(
      (best, notice) => (LEVEL_RANK[notice.level] < LEVEL_RANK[best] ? notice.level : best),
      items[0].level,
    );
    const texts = types?.[code];
    const merged = mergeSummaries(items.map((notice) => notice.affected_summary ?? []));
    const totalParts = [
      ...merged.documents.map((item) => documentLabel(item.document_type, item.count)),
      ...merged.quantities.map((item) => `${formatEsNumber(item.quantity, { maximumFractionDigits: 0 })} ${item.unit}`),
    ];
    const fillable = items.some((notice) => notice.fill_row || (notice.fill_rows ?? []).length > 0);
    return {
      key: code,
      code,
      title: noticeTypeName(items[0]),
      level,
      notices: items,
      affectedTotal: items.reduce((total, notice) => total + (notice.affected_count ?? 0), 0),
      newCount: items.filter((notice) => notice.is_new).length,
      // Sin texto común del tipo, el del aviso si todos dicen lo mismo.
      what: texts?.what ?? commonText(items.map((notice) => notice.missing_link)) ?? items[0].missing_link,
      todo: texts?.todo ?? commonText(items.map((notice) => notice.action)) ?? items[0].action,
      totalText: totalParts.length > 0 ? `Afecta a ${totalParts.join(' · ')}` : '',
      missingTable: fillable ? MISSING_TABLE_BY_CODE[code] ?? null : null,
    };
  });
  return groups.sort(
    (a, b) =>
      LEVEL_RANK[a.level] - LEVEL_RANK[b.level] ||
      b.affectedTotal - a.affectedTotal ||
      a.title.localeCompare(b.title, 'es'),
  );
}

export type NoticeSortKey = 'element' | 'what' | 'affects' | 'new';
export type SortDirection = 'asc' | 'desc';

/** Busca por código o nombre del elemento (y su título). */
export function filterNotices(notices: PlantDataNotice[], search: string): PlantDataNotice[] {
  const needle = search.trim().toLocaleLowerCase('es');
  if (!needle) return notices;
  return notices.filter((notice) =>
    `${notice.subject?.label ?? ''} ${notice.subject?.key ?? ''} ${notice.title}`.toLocaleLowerCase('es').includes(needle),
  );
}

/** «Afecta a»: por documentos, después cantidad y elemento; sin afectados, al final. */
export function sortNotices(
  notices: PlantDataNotice[],
  key: NoticeSortKey,
  direction: SortDirection,
): PlantDataNotice[] {
  const factor = direction === 'asc' ? 1 : -1;
  const label = (notice: PlantDataNotice) => notice.subject?.label ?? '';
  return [...notices].sort((a, b) => {
    if (key === 'affects') {
      const aEmpty = documentsCount(a) === 0;
      const bEmpty = documentsCount(b) === 0;
      if (aEmpty !== bEmpty) return aEmpty ? 1 : -1;
      return (
        factor * (documentsCount(a) - documentsCount(b)) ||
        factor * (quantityTotal(a) - quantityTotal(b)) ||
        label(a).localeCompare(label(b), 'es')
      );
    }
    if (key === 'new') {
      return factor * (Number(Boolean(a.is_new)) - Number(Boolean(b.is_new))) || label(a).localeCompare(label(b), 'es');
    }
    const text = (notice: PlantDataNotice) => (key === 'element' ? label(notice) : notice.missing_link);
    return factor * text(a).localeCompare(text(b), 'es');
  });
}

function csvCell(value: string): string {
  // Un valor que empiece por = + - @ no se ejecuta como fórmula en Excel.
  const safe = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
  return `"${safe.replace(/"/g, '""')}"`;
}

/** Lista completa del tipo (una fila por aviso), separada por «;». */
export function noticeTypeCsv(group: NoticeTypeGroup): string {
  const header = ['Elemento', 'Qué pasa', 'Afecta a', 'Nuevo', 'Qué hacer'];
  const rows = group.notices.map((notice) => [
    notice.subject?.label ?? '',
    notice.missing_link,
    affectedSummaryText(notice.affected_summary),
    notice.is_new ? 'sí' : '',
    notice.action,
  ]);
  return [header, ...rows].map((row) => row.map(csvCell).join(';')).join('\r\n');
}
